/**
 * Content-level spam scoring for the email template builder.
 *
 * Honest scope: content is a SECONDARY deliverability signal. Placement is
 * dominated by authentication (SPF, DKIM, DMARC), spam complaint rate, one
 * click unsubscribe, and sender reputation, all of which OTC handles at the
 * platform level (verified sending domains, injected List-Unsubscribe headers,
 * suppression). This score covers only what the author controls in the editor:
 * wording, structure, and links. The checks mirror what content filters still
 * weigh: trigger words, shouting, punctuation, image heavy layouts, and URL
 * shorteners.
 *
 * Pure function, no imports, unit tested in tests/spam-score.test.js.
 */

// Curated trigger list: classic money, urgency, and prize phrases that content
// filters still score. Deliberately short; published lists run to hundreds of
// terms with steep diminishing returns, and venue emails legitimately use
// words like "book now". Matched case insensitively as substrings.
const TRIGGER_WORDS = [
  'free!!',
  'act now',
  'act immediately',
  'urgent',
  'winner',
  'congratulations',
  'you have been selected',
  'you were selected',
  'claim your prize',
  'claim now',
  'risk-free',
  'risk free',
  '100% free',
  '100% guaranteed',
  'guarantee',
  'no obligation',
  'no strings attached',
  'limited time only',
  'once in a lifetime',
  'double your',
  'earn money',
  'make money fast',
  'cash bonus',
  'lowest price ever',
  'best price',
  'cheap',
  'this is not spam',
  'not junk',
  '$$$',
  '!!!',
]

// Public URL shorteners. Filters treat them with structural suspicion because
// phishers abuse them; there is no legitimate reason to shorten a link in email.
const URL_SHORTENER_PATTERN = /(https?:\/\/)?(www\.)?(bit\.ly|tinyurl\.com|t\.co|goo\.gl|ow\.ly|is\.gd|buff\.ly|rebrand\.ly|cutt\.ly|rb\.gy|shorturl\.at)\//i

// Per check point caps. The denominator is their sum, so 0 to 100 is a real
// range instead of an aspirational one.
const CAPS = {
  subjectCapsRatio: 3,
  subjectTriggers: 8,
  subjectPunctuation: 1.5,
  subjectLength: 1,
  bodyTriggers: 6,
  imageHeavy: 3,
  urlShorteners: 3,
}
const MAX_POINTS = Object.values(CAPS).reduce((a, b) => a + b, 0)

function findTriggers(text) {
  const haystack = (text || '').toLowerCase()
  return TRIGGER_WORDS.filter(word => haystack.includes(word))
}

/**
 * Score email content. All inputs are optional.
 *
 * @param {object} input
 * @param {string} input.subject       subject line
 * @param {string} input.bodyText      plain text of the email body
 * @param {string} input.html          full generated HTML (used for link checks)
 * @param {number} input.imageCount    number of image bearing components
 * @returns {{ score: number, riskLevel: string, riskColor: string, triggers: string[] }}
 */
export function scoreEmailContent({ subject = '', bodyText = '', html = '', imageCount = 0 } = {}) {
  let points = 0
  const triggers = []

  // Subject: shouting
  const letters = subject.replace(/[^a-zA-Z]/g, '')
  if (letters.length >= 5) {
    const capsRatio = (subject.match(/[A-Z]/g) || []).length / letters.length
    if (capsRatio > 0.5) {
      points += CAPS.subjectCapsRatio
      triggers.push('Mostly capital letters in subject')
    }
  }

  // Subject: trigger words
  const subjectHits = findTriggers(subject)
  if (subjectHits.length > 0) {
    points += Math.min(CAPS.subjectTriggers, subjectHits.length * 2)
    triggers.push(`Spam wording in subject: ${subjectHits.slice(0, 3).join(', ')}`)
  }

  // Subject: punctuation
  if ((subject.match(/!/g) || []).length > 1) {
    points += CAPS.subjectPunctuation
    triggers.push('Multiple exclamation marks in subject')
  }

  // Subject: length
  if (subject.length > 60) {
    points += CAPS.subjectLength
    triggers.push('Subject longer than 60 characters')
  }

  // Body: trigger words
  const bodyHits = findTriggers(bodyText)
  if (bodyHits.length > 0) {
    points += Math.min(CAPS.bodyTriggers, bodyHits.length)
    triggers.push(`Spam wording in content: ${bodyHits.slice(0, 3).join(', ')}`)
  }

  // Body: image heavy with little text. Spammers hide text in images, so
  // filters distrust image dominated emails.
  const textLength = (bodyText || '').replace(/\s+/g, ' ').trim().length
  if (imageCount > 0 && textLength < 200) {
    points += CAPS.imageHeavy
    triggers.push('Images with very little text')
  }

  // Links: public URL shorteners
  if (URL_SHORTENER_PATTERN.test(html)) {
    points += CAPS.urlShorteners
    triggers.push('Shortened links (use full URLs instead)')
  }

  const score = Math.max(0, 100 - Math.round((points / MAX_POINTS) * 100))

  let riskLevel = 'Low'
  let riskColor = '#10b981'
  if (score < 50) {
    riskLevel = 'High'
    riskColor = '#ef4444'
  } else if (score < 75) {
    riskLevel = 'Medium'
    riskColor = '#f59e0b'
  }

  return { score, riskLevel, riskColor, triggers: triggers.slice(0, 3) }
}
